import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Users,
  UserPlus,
  Settings,
  Copy,
  Download,
  Trash2,
  Trash,
  CheckCircle2,
  AlertTriangle,
  Info,
  XCircle,
  Search,
  Lock,
  Unlock,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { api } from '@/lib/api'
import type { AdminUser, UserRole, BatchCreateUsersResponse } from '@/types'
import * as XLSX from 'xlsx'

interface EditableUserState {
  displayName: string
  role: UserRole
  password: string
  level: number
}

type FeedbackType = 'success' | 'error' | 'info' | 'warning'

interface FeedbackState {
  type: FeedbackType
  title: string
  description?: string
}

interface ConfirmDialogState {
  open: boolean
  title: string
  description: string
  confirmLabel?: string
  tone?: 'danger' | 'default'
  onConfirm: () => void
}

type BatchFormState = {
  prefix: string
  count: number
  startIndex: number
  passwordLength: number
  level: number
  role: UserRole
}
type BatchFormNumericField = 'count' | 'startIndex' | 'passwordLength' | 'level'

export function UserManagementPage() {
  const DEFAULT_LEVEL = 100
  const queryClient = useQueryClient()

  const usersQuery = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.getUsers(),
  })

  const [newUser, setNewUser] = useState({
    username: '',
    displayName: '',
    password: '',
    role: 'user' as UserRole,
    level: DEFAULT_LEVEL,
  })
  const [editingUsers, setEditingUsers] = useState<Record<string, EditableUserState>>({})
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set())
  const [batchForm, setBatchForm] = useState<BatchFormState>({
    prefix: '',
    count: 10,
    startIndex: 1,
    passwordLength: 12,
    level: DEFAULT_LEVEL,
    role: 'user',
  })
  const [batchResult, setBatchResult] = useState<BatchCreateUsersResponse | null>(null)
  const [feedback, setFeedback] = useState<FeedbackState | null>(null)
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const batchCardRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!feedback) return
    const timer = window.setTimeout(() => setFeedback(null), 5000)
    return () => window.clearTimeout(timer)
  }, [feedback])

  const showFeedback = (type: FeedbackType, title: string, description?: string) => {
    setFeedback({ type, title, description })
  }

  const openConfirmDialog = (dialog: Omit<ConfirmDialogState, 'open'>) => {
    setConfirmDialog({ open: true, ...dialog })
  }

  const openCreateModal = () => {
    setNewUser({
      username: '',
      displayName: '',
      password: '',
      role: 'user',
      level: DEFAULT_LEVEL,
    })
    setIsCreateModalOpen(true)
  }

  const closeCreateModal = () => {
    if (!createUserMutation.isPending) {
      setIsCreateModalOpen(false)
    }
  }

  const scrollToBatchSection = () => {
    batchCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const resolveErrorMessage = (error: unknown, fallback: string): string => {
    if (!error) return fallback
    if (typeof error === 'string') return error
    if (typeof error === 'object') {
      const e = error as Record<string, unknown>
      if (e['code'] === 'DUPLICATE') return '用户名已存在，请换一个。'
      if (e['code'] === 'LAST_ADMIN') return '系统至少需要保留一名管理员，无法删除或降级最后一名管理员。'
      if (e['code'] === 'VALIDATION_ERROR' && typeof e['message'] === 'string') return e['message']
      if (e['code'] === 'ALREADY_LOGGED_IN') return '该账号已在其他设备登录，请先从旧设备退出。'
      if (typeof e['message'] === 'string') {
        if (e['message'].includes('UNIQUE constraint failed')) {
          return '用户名已存在，请换一个。'
        }
        return e['message']
      }
    }
    return fallback
  }

  const feedbackThemes: Record<FeedbackType, { container: string; icon: JSX.Element }> = {
    success: {
      container: 'border-green-200 bg-green-50 text-green-800',
      icon: <CheckCircle2 className="h-5 w-5 text-green-600" />,
    },
    error: {
      container: 'border-red-200 bg-red-50 text-red-800',
      icon: <XCircle className="h-5 w-5 text-red-600" />,
    },
    info: {
      container: 'border-blue-200 bg-blue-50 text-blue-800',
      icon: <Info className="h-5 w-5 text-blue-600" />,
    },
    warning: {
      container: 'border-amber-200 bg-amber-50 text-amber-800',
      icon: <AlertTriangle className="h-5 w-5 text-amber-600" />,
    },
  }

  useEffect(() => {
    if (usersQuery.data) {
      const nextState: Record<string, EditableUserState> = {}
      usersQuery.data.users.forEach((user) => {
        nextState[user.id] = {
          displayName: user.displayName || '',
          role: user.role,
          password: '',
          level: user.level ?? 0,
        }
      })
      setEditingUsers(nextState)
    }
  }, [usersQuery.data])

  const createUserMutation = useMutation({
    mutationFn: api.createUser.bind(api),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setNewUser({ username: '', displayName: '', password: '', role: 'user', level: DEFAULT_LEVEL })
       setIsCreateModalOpen(false)
      showFeedback('success', '创建成功', '新用户已添加至列表。')
    },
    onError: (error: unknown) => {
      showFeedback('error', '创建用户失败', resolveErrorMessage(error, '请稍后再试。'))
    },
  })

  const updateUserMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { displayName?: string | null; role?: UserRole; password?: string } }) =>
      api.updateUser(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      showFeedback('success', '已保存更改', '用户信息更新成功。')
    },
    onError: (error: unknown) => {
      showFeedback('error', '更新用户失败', resolveErrorMessage(error, '请稍后再试。'))
    },
  })

  const deleteUserMutation = useMutation({
    mutationFn: api.deleteUser.bind(api),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setSelectedUserIds(new Set())
      showFeedback('success', '账号已删除', '用户及其关联数据已清理。')
    },
    onError: (error: unknown) => {
      showFeedback('error', '删除用户失败', resolveErrorMessage(error, '请稍后再试。'))
    },
  })

  const batchDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => api.batchDeleteUsers(ids),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setSelectedUserIds(new Set())
      showFeedback('success', '批量删除完成', `共请求 ${data.requested} 个账号，成功删除 ${data.deleted} 个。`)
    },
    onError: (error: unknown) => {
      showFeedback('error', '批量删除失败', resolveErrorMessage(error, '请稍后再试。'))
    },
  })

  const batchCreateMutation = useMutation({
    mutationFn: api.batchCreateUsers.bind(api),
    onSuccess: (data: BatchCreateUsersResponse) => {
      setBatchResult(data)
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setBatchForm((prev) => ({
        ...prev,
        prefix: prev.prefix,
        startIndex: prev.startIndex + prev.count,
      }))
      showFeedback('success', '批量生成成功', `新增 ${data.meta.created} 个账号，请及时下载或复制初始密码。`)
    },
    onError: (error: unknown) => {
      showFeedback('error', '批量创建失败', resolveErrorMessage(error, '请稍后再试。'))
    },
  })

  const lockUserMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string | null }) =>
      api.lockUser(id, reason ?? undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      showFeedback('success', '账号已锁定', '该账号已被锁定，无法登录系统。')
    },
    onError: (error: unknown) => {
      showFeedback('error', '锁定账号失败', resolveErrorMessage(error, '请稍后再试。'))
    },
  })

  const unlockUserMutation = useMutation({
    mutationFn: (id: string) => api.unlockUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      showFeedback('success', '账号已解锁', '该账号已恢复正常登录。')
    },
    onError: (error: unknown) => {
      showFeedback('error', '解除锁定失败', resolveErrorMessage(error, '请稍后再试。'))
    },
  })

  const lockingUserId = (lockUserMutation.variables as { id: string } | undefined)?.id ?? null
  const unlockingUserId = (unlockUserMutation.variables as string | undefined) ?? null

  const users = usersQuery.data?.users || []
  const normalizedSearch = searchTerm.trim().toLowerCase()
  const filteredUsers = normalizedSearch.length === 0
    ? users
    : users.filter((user) => {
        const username = user.username.toLowerCase()
        const displayName = (user.displayName || '').toLowerCase()
        return username.includes(normalizedSearch) || displayName.includes(normalizedSearch)
      })
  const allVisibleSelected = filteredUsers.length > 0 && filteredUsers.every((user) => selectedUserIds.has(user.id))
  const selectedCount = selectedUserIds.size

  const handleUserFieldChange = (id: string, field: keyof EditableUserState, value: string | UserRole) => {
    setEditingUsers((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }))
  }

  const handleUserLevelChange = (id: string, value: string) => {
    const parsed = Number.parseInt(value, 10)
    const safeLevel = Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_LEVEL
    setEditingUsers((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        level: safeLevel,
      },
    }))
  }

  const handleBatchFieldChange = <K extends keyof BatchFormState>(field: K, value: BatchFormState[K]) => {
    setBatchForm((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleBatchNumberChange = (field: BatchFormNumericField, value: string) => {
    const parsed = Number.parseInt(value, 10)
    setBatchForm((prev) => ({
      ...prev,
      [field]: Number.isFinite(parsed) ? parsed : prev[field],
    }))
  }

  const toggleSelectUser = (id: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleSelectAllVisible = () => {
    if (!filteredUsers.length) return
    setSelectedUserIds((prev) => {
      const next = new Set(prev)
      if (allVisibleSelected) {
        filteredUsers.forEach((user) => next.delete(user.id))
      } else {
        filteredUsers.forEach((user) => next.add(user.id))
      }
      return next
    })
  }

  const handleCreateUser = () => {
    if (!newUser.username.trim()) {
      showFeedback('warning', '请输入用户名', '用户名不能为空。')
      return
    }
    if (newUser.password.length < 8) {
      showFeedback('warning', '密码长度不足', '新用户密码至少需要 8 位字符。')
      return
    }
    if (!Number.isInteger(newUser.level) || newUser.level < 0) {
      showFeedback('warning', '等级格式不正确', '等级必须是非负整数。')
      return
    }

    createUserMutation.mutate({
      username: newUser.username.trim(),
      password: newUser.password,
      displayName: newUser.displayName.trim() || undefined,
      role: newUser.role,
      level: newUser.level,
    })
  }

  const handleDeleteUser = (id: string) => {
    const target = users.find((user) => user.id === id)
    if (!target) return
    openConfirmDialog({
      title: '确认删除账号',
      description: `确定要删除账号「${target.username}」吗？此操作不可恢复。`,
      confirmLabel: '确认删除',
      tone: 'danger',
      onConfirm: () => deleteUserMutation.mutate(id),
    })
  }

  const handleBatchDelete = () => {
    if (selectedCount === 0) {
      showFeedback('warning', '未选择用户', '请勾选需要删除的账号后再操作。')
      return
    }
    openConfirmDialog({
      title: '批量删除账号',
      description: `确定要删除选中的 ${selectedCount} 个账号吗？此操作不可恢复。`,
      confirmLabel: '批量删除',
      tone: 'danger',
      onConfirm: () => batchDeleteMutation.mutate(Array.from(selectedUserIds)),
    })
  }

  const handleBatchCreate = () => {
    if (!batchForm.prefix.trim()) {
      showFeedback('warning', '请输入账号前缀', '前缀不能为空。')
      return
    }
    if (!Number.isInteger(batchForm.count) || batchForm.count <= 0 || batchForm.count > 100) {
      showFeedback('warning', '批量数量不合法', '请输入 1-100 之间的整数。')
      return
    }
    if (!Number.isInteger(batchForm.startIndex) || batchForm.startIndex <= 0) {
      showFeedback('warning', '起始编号不合法', '起始编号必须是正整数。')
      return
    }
    if (!Number.isInteger(batchForm.passwordLength) || batchForm.passwordLength < 8) {
      showFeedback('warning', '密码长度不足', '初始密码长度至少 8 位。')
      return
    }
    if (!Number.isInteger(batchForm.level) || batchForm.level < 0) {
      showFeedback('warning', '等级格式不正确', '等级必须是非负整数。')
      return
    }

    batchCreateMutation.mutate({
      prefix: batchForm.prefix.trim(),
      count: batchForm.count,
      startIndex: batchForm.startIndex,
      passwordLength: batchForm.passwordLength,
      level: batchForm.level,
      role: batchForm.role,
    })
  }

  const handleCopyBatchCredentials = async () => {
    if (!batchResult || batchResult.created.length === 0) return
    const lines = batchResult.created
      .map((item, index) => `${index + 1}. ${item.username}\t${item.password}`)
      .join('\n')
    try {
      if (navigator?.clipboard) {
        await navigator.clipboard.writeText(lines)
        showFeedback('success', '复制成功', '账号和密码已复制到剪贴板。')
      } else {
        throw new Error('clipboard unavailable')
      }
    } catch {
      showFeedback('error', '复制失败', '请手动选择表格内容进行复制。')
    }
  }

  const handleExportBatchExcel = () => {
    if (!batchResult || batchResult.created.length === 0) {
      showFeedback('info', '暂无数据', '请先批量生成账号后再导出。')
      return
    }

    const workbook = XLSX.utils.book_new()
    const data = batchResult.created.map((item, index) => ({
      序号: index + 1,
      用户名: item.username,
      初始密码: item.password,
    }))
    const sheet = XLSX.utils.json_to_sheet(data)
    XLSX.utils.book_append_sheet(workbook, sheet, '账号列表')

    if (batchResult.duplicates.length > 0) {
      const duplicateSheet = XLSX.utils.aoa_to_sheet([
        ['以下用户名已存在，未重复创建'],
        ...batchResult.duplicates.map((name) => [name]),
      ])
      XLSX.utils.book_append_sheet(workbook, duplicateSheet, '已存在用户名')
    }

    const metaSheet = XLSX.utils.aoa_to_sheet([
      ['批量创建配置'],
      ['前缀', batchResult.meta.prefix],
      ['生成数量', batchResult.meta.requested],
      ['实际创建', batchResult.meta.created],
      ['起始编号', batchResult.meta.startIndex],
      ['角色', batchResult.meta.role === 'admin' ? '管理员' : '普通用户'],
      ['等级', batchResult.meta.level],
      ['密码长度', batchResult.meta.passwordLength],
      ['生成时间', new Date().toLocaleString()],
    ])
    XLSX.utils.book_append_sheet(workbook, metaSheet, '生成说明')

    const workbookArray = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([workbookArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
    link.href = url
    link.download = `批量账号_${batchResult.meta.prefix}_${timestamp}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    showFeedback('success', '导出成功', 'Excel 文件已开始下载。')
  }

  const handleUpdateUser = (user: AdminUser) => {
    const pending = editingUsers[user.id]
    if (!pending) return

    const payload: { displayName?: string | null; role?: UserRole; password?: string; level?: number } = {}
    if (pending.displayName !== (user.displayName || '')) {
      payload.displayName = pending.displayName.trim() || null
    }
    if (pending.role !== user.role) {
      payload.role = pending.role
    }
    if (pending.password) {
      if (pending.password.length < 8) {
        showFeedback('warning', '密码长度不足', '密码至少需要 8 位字符。')
        return
      }
      payload.password = pending.password
    }

    if (pending.level !== user.level) {
      if (!Number.isInteger(pending.level) || pending.level < 0) {
        showFeedback('warning', '等级格式不正确', '等级必须是非负整数。')
        return
      }
      payload.level = pending.level
    }

    if (!Object.keys(payload).length) {
      showFeedback('info', '无需更新', '请修改信息后再保存。')
      return
    }

    updateUserMutation.mutate({ id: user.id, payload })
    setEditingUsers((prev) => ({
      ...prev,
      [user.id]: {
        ...prev[user.id],
        password: '',
      },
    }))
  }

  const handleLockUser = (user: AdminUser) => {
    const reason = typeof window !== 'undefined'
      ? window.prompt('请输入锁定原因（可留空）', user.lock?.reason ?? '')
      : ''
    if (reason === null) return
    lockUserMutation.mutate({ id: user.id, reason: reason ?? undefined })
  }

  const handleUnlockUser = (user: AdminUser) => {
    const confirmed = typeof window === 'undefined'
      ? true
      : window.confirm(`确定要解除账号「${user.username}」的锁定状态吗？`)
    if (!confirmed) return
    unlockUserMutation.mutate(user.id)
  }

  const isDeleting = deleteUserMutation.isPending || batchDeleteMutation.isPending

  return (
    <div className="space-y-8">
      {confirmDialog?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="mt-1">
                {confirmDialog.tone === 'danger' ? (
                  <Trash2 className="h-6 w-6 text-red-500" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-amber-500" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900">{confirmDialog.title}</h3>
                <p className="mt-2 text-sm text-gray-600">{confirmDialog.description}</p>
              </div>
              <button
                type="button"
                onClick={() => setConfirmDialog(null)}
                aria-label="关闭确认对话框"
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setConfirmDialog(null)} disabled={isDeleting}>
                取消
              </Button>
              <Button
                onClick={() => {
                  confirmDialog.onConfirm()
                  setConfirmDialog(null)
                }}
                loading={isDeleting}
                className={
                  confirmDialog.tone === 'danger'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : undefined
                }
              >
                {confirmDialog.confirmLabel ?? '确认'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {feedback && (
        <div className={`fixed top-6 right-6 z-50 w-full max-w-sm rounded-2xl border p-4 shadow-lg transition-all duration-200 ${feedbackThemes[feedback.type].container}`}>
          <div className="flex items-start gap-3">
            <div className="mt-0.5">{feedbackThemes[feedback.type].icon}</div>
            <div className="flex-1">
              <p className="text-sm font-semibold">{feedback.title}</p>
              {feedback.description && (
                <p className="mt-1 text-xs opacity-80">{feedback.description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setFeedback(null)}
              aria-label="关闭提示"
              className="text-current"
            >
              <XCircle className="h-4 w-4 opacity-60 hover:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>
      )}

      {isCreateModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-3xl border border-purple-100 bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">新增用户</h3>
                <p className="mt-1 text-sm text-gray-500">填写账号信息并分配角色与初始密码。</p>
              </div>
              <button
                type="button"
                onClick={closeCreateModal}
                aria-label="关闭新增用户弹窗"
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <Input
                label="用户名 *"
                value={newUser.username}
                onChange={(e) => setNewUser((prev) => ({ ...prev, username: e.target.value }))}
                placeholder="例如：admin"
                autoFocus
              />
              <Input
                label="显示名称"
                value={newUser.displayName}
                onChange={(e) => setNewUser((prev) => ({ ...prev, displayName: e.target.value }))}
                placeholder="管理员名称"
              />
              <Input
                label="初始密码 *"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser((prev) => ({ ...prev, password: e.target.value }))}
                placeholder="至少 8 位"
              />
              <Input
                label="用户等级"
                type="number"
                min={0}
                value={Number.isFinite(newUser.level) ? newUser.level : DEFAULT_LEVEL}
                onChange={(e) => {
                  const parsed = Number.parseInt(e.target.value, 10)
                  setNewUser((prev) => ({ ...prev, level: Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_LEVEL }))
                }}
                helper="数字越小权限越高，建议普通用户设置为 100"
              />
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-gray-700">角色 *</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, role: e.target.value as UserRole }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="admin">管理员</option>
                  <option value="user">普通用户</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="secondary" onClick={closeCreateModal} disabled={createUserMutation.isPending}>
                取消
              </Button>
              <Button
                onClick={handleCreateUser}
                loading={createUserMutation.isPending}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                创建用户
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 via-pink-600 to-rose-600 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-black/5" />
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm">
              <Users className="h-4 w-4" /> 用户管理中心
            </div>
            <h1 className="text-3xl font-bold lg:text-4xl">用户管理</h1>
            <p className="max-w-3xl text-sm text-white/90 lg:text-base">
              管理后台用户账户、权限角色与安全设置
            </p>
          </div>
          <div className="rounded-2xl bg-white/15 p-6 text-center backdrop-blur-md">
            <p className="text-3xl font-bold">{users.length}</p>
            <p className="mt-1 text-sm text-white/80">活跃用户</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="搜索用户名或显示名称"
            className="w-full rounded-full border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
          />
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button
            onClick={openCreateModal}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            新增用户
          </Button>
          <Button variant="secondary" onClick={scrollToBatchSection}>
            批量生成账号
          </Button>
        </div>
      </div>

      <Card ref={batchCardRef} className="border-gray-100 shadow-xl">
        <CardHeader className="border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-bold">批量生成账号</CardTitle>
              <p className="text-sm text-gray-600 mt-1">支持一键生成连续账号，初始密码自动随机分配。</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Input
              label="账号前缀"
              value={batchForm.prefix}
              onChange={(e) => handleBatchFieldChange('prefix', e.target.value)}
              placeholder="例如: classA"
              required
            />
            <Input
              label="生成数量"
              type="number"
              min={1}
              value={batchForm.count}
              onChange={(e) => handleBatchNumberChange('count', e.target.value)}
              helper="单次最多生成 100 个"
            />
            <Input
              label="起始编号"
              type="number"
              min={1}
              value={batchForm.startIndex}
              onChange={(e) => handleBatchNumberChange('startIndex', e.target.value)}
              helper="例如从 1 开始"
            />
            <Input
              label="密码长度"
              type="number"
              min={8}
              value={batchForm.passwordLength}
              onChange={(e) => handleBatchNumberChange('passwordLength', e.target.value)}
              helper="至少 8 位"
            />
            <Input
              label="等级"
              type="number"
              min={0}
              value={batchForm.level}
              onChange={(e) => handleBatchNumberChange('level', e.target.value)}
              helper="数值越小权限越高"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">角色</label>
              <select
                value={batchForm.role}
                onChange={(e) => handleBatchFieldChange('role', e.target.value as UserRole)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="user">普通用户</option>
                <option value="admin">管理员</option>
              </select>
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-500">生成的账号将按前缀 + 连续编号命名，例如 <span className="font-mono">{`${batchForm.prefix || 'prefix'}${String(batchForm.startIndex).padStart(2, '0')}`}</span>。</p>
            <Button
              onClick={handleBatchCreate}
              loading={batchCreateMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
            >
              批量生成账号
            </Button>
          </div>

          {batchResult && (
            <div className="space-y-4 rounded-lg border border-blue-100 bg-blue-50/60 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-blue-900">
                    已成功创建 {batchResult.meta.created} / {batchResult.meta.requested} 个账号
                  </p>
                  <p className="text-xs text-blue-800">
                    角色：{batchResult.meta.role === 'admin' ? '管理员' : '普通用户'} · 等级：{batchResult.meta.level} · 密码长度：{batchResult.meta.passwordLength}
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleCopyBatchCredentials}
                    disabled={!batchResult.created.length}
                  >
                    <Copy className="mr-2 h-4 w-4" />复制账号密码
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleExportBatchExcel}
                    disabled={!batchResult.created.length}
                  >
                    <Download className="mr-2 h-4 w-4" />导出 Excel
                  </Button>
                </div>
              </div>

              {batchResult.duplicates.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                  以下用户名已存在，未重复创建：
                  <span className="ml-2 font-mono">{batchResult.duplicates.join(', ')}</span>
                </div>
              )}

              <div className="overflow-x-auto rounded-lg border border-white/60 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-blue-100">
                  <thead className="bg-blue-100/60">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-blue-900">序号</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-blue-900">用户名</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-blue-900">初始密码</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-50">
                    {batchResult.created.map((item, index) => (
                      <tr key={item.id} className="bg-white hover:bg-blue-50/70">
                        <td className="px-4 py-2 text-sm text-gray-700">{index + 1}</td>
                        <td className="px-4 py-2 font-mono text-sm text-gray-900">{item.username}</td>
                        <td className="px-4 py-2 font-mono text-sm text-gray-900">{item.password}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-gray-100 shadow-xl">
        <CardHeader className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-gray-100/50">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-gray-100 p-2">
              <Settings className="h-5 w-5 text-gray-600" />
            </div>
            <CardTitle className="text-lg font-bold">现有用户列表</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 p-0">
          <div className="flex flex-col gap-2 px-6 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-500">
              显示 {filteredUsers.length} / {users.length} 个账号 · 已选 {selectedCount} 个
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button
                variant="secondary"
                size="sm"
                onClick={toggleSelectAllVisible}
                disabled={!filteredUsers.length}
              >
                <Users className="mr-2 h-4 w-4" />
                {allVisibleSelected ? '取消本页全选' : '全选当前列表'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleBatchDelete}
                disabled={selectedCount === 0 || batchDeleteMutation.isPending}
                className="bg-red-50 text-red-600 hover:bg-red-100"
              >
                <Trash className="mr-2 h-4 w-4" />
                删除选中
              </Button>
            </div>
          </div>

          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr className="border-b border-gray-200">
                <th className="w-12 px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    disabled={!filteredUsers.length}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">用户名</th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">显示名称</th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">角色</th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">等级</th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">账号状态</th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">重设密码</th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">操作</th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">删除</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-sm text-gray-500">
                    未找到符合条件的账号，请尝试调整搜索关键词。
                  </td>
                </tr>
              )}
              {filteredUsers.map((user, index) => {
                const pending = editingUsers[user.id]
                return (
                  <tr key={user.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} transition-all hover:bg-purple-50/30 hover:shadow-md`}>
                    <td className="px-4 py-4 align-top">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.has(user.id)}
                        onChange={() => toggleSelectUser(user.id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white font-bold shadow-md">
                          {user.username[0].toUpperCase()}
                        </div>
                        <span className="text-sm font-semibold text-gray-900">{user.username}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <Input
                        value={pending?.displayName ?? ''}
                        onChange={(e) => handleUserFieldChange(user.id, 'displayName', e.target.value)}
                        placeholder="显示名称"
                        className="bg-white/50"
                      />
                    </td>
                    <td className="px-6 py-4 align-top">
                      <select
                        value={pending?.role ?? user.role}
                        onChange={(e) => handleUserFieldChange(user.id, 'role', e.target.value as UserRole)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                      >
                        <option value="admin">管理员</option>
                        <option value="user">普通用户</option>
                      </select>
                      <div className="mt-2">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}>
                          当前: {user.role === 'admin' ? '管理员' : '普通用户'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <Input
                        type="number"
                        min={0}
                        value={pending?.level ?? user.level ?? DEFAULT_LEVEL}
                        onChange={(e) => handleUserLevelChange(user.id, e.target.value)}
                        helper="数字越小权限越高"
                        className="bg-white/50"
                      />
                    </td>
                    <td className="px-6 py-4 align-top">
                      {user.lock ? (
                        <div className="space-y-2">
                          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">
                            已锁定
                          </span>
                          {user.lock.reason && (
                            <p className="text-xs text-red-600">原因：{user.lock.reason}</p>
                          )}
                          <p className="text-xs text-gray-500">
                            锁定时间：{new Date(user.lock.lockedAt).toLocaleString()}
                          </p>
                          {(user.lock.lockedByName || user.lock.lockedById) && (
                            <p className="text-xs text-gray-500">
                              操作人：{user.lock.lockedByName ?? user.lock.lockedById}
                            </p>
                          )}
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleUnlockUser(user)}
                            loading={unlockUserMutation.isPending && unlockingUserId === user.id}
                            disabled={
                              (lockUserMutation.isPending && lockingUserId !== user.id) ||
                              (unlockUserMutation.isPending && unlockingUserId !== user.id)
                            }
                            className="bg-red-50 text-red-600 hover:bg-red-100"
                          >
                            <Unlock className="mr-2 h-4 w-4" />
                            解除锁定
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                            正常
                          </span>
                          <p className="text-xs text-gray-500">
                            最近更新：{new Date(user.updatedAt).toLocaleString()}
                          </p>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleLockUser(user)}
                            loading={lockUserMutation.isPending && lockingUserId === user.id}
                            disabled={
                              (unlockUserMutation.isPending && unlockingUserId !== user.id) ||
                              (lockUserMutation.isPending && lockingUserId !== user.id)
                            }
                            className="bg-amber-50 text-amber-700 hover:bg-amber-100"
                          >
                            <Lock className="mr-2 h-4 w-4" />
                            锁定账号
                          </Button>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 align-top">
                      <Input
                        type="password"
                        value={pending?.password ?? ''}
                        onChange={(e) => handleUserFieldChange(user.id, 'password', e.target.value)}
                        placeholder="留空表示不修改"
                        className="bg-white/50"
                      />
                    </td>
                    <td className="px-6 py-4 align-top">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleUpdateUser(user)}
                        loading={updateUserMutation.isPending}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md"
                      >
                        保存更改
                      </Button>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDeleteUser(user.id)}
                        loading={deleteUserMutation.isPending}
                        className="bg-red-50 text-red-600 hover:bg-red-100"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        删除
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  )
}

export default UserManagementPage
